import React, { ReactElement, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Form, message } from "antd"
import KosmoButton from "@/UI/Buttons/KosmoButton"
import ModalCard from "@/UI/Modals/ModalCard"
import { IWebhook } from "@/types/webhooks.types"
import { testWebhook } from "@/api/webhooks"
import { WebhookUrlFormItem } from "@/UI/Forms/FormItems/WebhookUrlFormItem"
import { RuleObject } from "antd/lib/form"

interface IWebhooksAddEditModalProps {
  title: string
  button: ReactElement
  handleSubmit: (values: IWebhook) => Promise<Error | null>
  webhook?: IWebhook
  webhooks: IWebhook[]
}

const WebhooksAddEditModal: React.FC<IWebhooksAddEditModalProps> = ({
  title,
  button,
  handleSubmit,
  webhook,
  webhooks,
}) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const onFinish = async () => {
    try {
      setIsLoading(true)
      const values: IWebhook = await form.validateFields()
      if (webhook) {
        values.id = webhook.id
      }
      await handleSubmit(values)
      message.success(
        webhook
          ? t("developers.WebhooksAddEditModal.successfullyUpdatedWebhook")
          : t("developers.WebhooksAddEditModal.successfullyAddedWebhook")
      )
      closeModal()
    } catch (error) {
      // message.error(webhook ? t("developers.WebhooksAddEditModal.errorUpdatingWebhook") : t("developers.WebhooksAddEditModal.errorAddingWebhook"))
      console.log(error)
    } finally {
      setIsLoading(false)
    }
  }
  const openModal = () => setIsModalVisible(true)
  const closeModal = () => setIsModalVisible(false)

  useEffect(() => {
    if (!isModalVisible) {
      form.resetFields()
    } else {
    }
  }, [isModalVisible])

  useEffect(() => {
    if (webhook) {
      form.setFieldsValue(webhook)
    }
  }, [])

  return (
    <>
      {React.cloneElement(button, { onClick: openModal })}
      <ModalCard closable isModalVisible={isModalVisible} onCancel={closeModal} title={title}>
        <Form id={"add-edit-webhook"} form={form} layout="vertical" onFinish={onFinish}>
          <WebhookUrlFormItem
            label={t("developers.WebhooksAddEditModal.webhookUrl")}
            name="url"
            placeholder={t("developers.WebhooksAddEditModal.enterYourUrl")}
            onChangeValidator={() => ({
              async validator(_: RuleObject, value: string) {
                const isAddingMatchingWebhook =
                  !webhook && !!webhooks?.find((webhook) => webhook.url === value)
                const isEditMatchingWebhook =
                  webhook &&
                  !!webhooks?.find((webhook) => webhook.url === value && webhook.id !== webhook.id)
                if (isAddingMatchingWebhook || isEditMatchingWebhook) {
                  return Promise.reject(new Error("You already have this webhook url"))
                }
                return Promise.resolve()
              },
              validateTrigger: "onChange",
            })}
            onSubmitValidator={() => ({
              async validator(_: RuleObject, value: string) {
                try {
                  setIsLoading(true)
                  await testWebhook({ url: value })
                  setIsLoading(false)
                } catch (error) {
                  setIsLoading(false)
                  return Promise.reject(new Error("Invalid webhook url"))
                }
                return Promise.resolve()
              },
              validateTrigger: "onSubmit",
            })}
          />
          <p className="text-gray-500 text-sm">{t("developers.Webhooks.addWebhookTestNote")}</p>
          <div className="flex w-full justify-end !mt-4 space-x-4">
            <KosmoButton type="default" onClick={closeModal}>
              {t("common.cancel")}
            </KosmoButton>
            <KosmoButton
              id="save-webhook-button"
              type="primary"
              htmlType="submit"
              isDisabled={isLoading}
            >
              {t("common.save")}
            </KosmoButton>
          </div>
        </Form>
      </ModalCard>
    </>
  )
}

export default WebhooksAddEditModal
